import { useEffect, useMemo, useState } from 'react';
import {
  relatedRooms,
  todayIsoParis,
  type ServiceGroupRoom,
  type ServiceListRow,
} from '@lemontaigu/staff';
import { useBoard } from '@lemontaigu/staff/react';
import { FrDateInput } from '../ui/FrDateInput.js';
import { shiftDay } from './logic.js';

// « Services » — the sheet the salle lays tables from, and the kitchen cooks to.
//
// Operator, 2026-09-09: *"add a way to print the list for people with diner
// (staff use this to prepare the tables for each party; name, room, Type,
// Séjour, num din) ordered by room"*, and the same for petits-déjeuners.
//
// ── What this is NOT ──────────────────────────────────────────────────────
//
// It is not the board. The board answers « how many » and is planned from; this
// answers « who » and is CARRIED — folded in an apron, marked up in pen, read
// at 19:45 by someone who is not near a screen. That is why it prints as two
// plain tables with no colour, no chips and no controls: everything that helps
// on screen is ink that helps nobody on paper.
//
// ── The columns are the operator's, including the ones that are absent ─────
//
// Name · Chb · Type · Séjour · number. Vega's own « Effectif Repas » screen
// also carries Nation, Pdj and Pmi; the operator crossed all three off the
// screenshot, so they are not here. A column nobody uses on a printed sheet is
// worse than clutter — it is one more thing to scan past when the service is
// starting.
//
// ── One row per PARTY ─────────────────────────────────────────────────────
//
// A group is one line reading « Grpe », because it is one party at one table
// block. The database enforces that grain (0147); this file only renders it.
// The failure it prevents is invisible on paper: a group printed once per room,
// every line individually correct, the total silently multiplied.
//
// Since 0158 that one line has a TABLE of its own at the bottom of the sheet —
// room and pdj, nothing else — because « 36 » is the right answer for the
// kitchen and no answer at all for whoever carries trays to twenty-three doors.
//
// ── A « day » here is a NIGHT, not a calendar date ────────────────────────
//
// Operator, 2026-09-12: *« for Day D show on top the diner for the day and at
// the bottom the breakfast for the following day »*.
//
// That is the shift, not the date. The people at dinner on the 12th are, very
// largely, the people at breakfast on the 13th — one service, read by one team,
// on one sheet. Printing the 12th's dinner beside the 12th's breakfast would
// pair each meal with the wrong guests and quietly make the sheet useless for
// the one thing it is for: knowing who is in the building tonight.
//
// So the breakfast half of this page is always `day + 1`, and BOTH halves state
// their own full date in their heading — on screen and on paper. A printed
// sheet with one date at the top and two different days of data on it is the
// kind of error nobody catches until the trays are wrong.

const DAY_LABEL = new Intl.DateTimeFormat('fr-FR', {
  weekday: 'long',
  day: 'numeric',
  month: 'long',
  year: 'numeric',
});

/** '2026-09-09' → 'mercredi 9 septembre 2026'. Parsed as parts, never `new
 *  Date('…')`, which reads an ISO date as UTC and can print the day before. */
export function longDayLabel(iso: string): string {
  const [y, m, d] = iso.split('-').map(Number);
  if (!y || !m || !d) return iso;
  return DAY_LABEL.format(new Date(y, m - 1, d));
}

/** '2026-09-09' → '09/09'. The séjour column is a span, not a timestamp: the
 *  year is the same on every line of a service and would only take width. */
export function shortDate(iso: string | null): string {
  if (!iso) return '';
  const [, m, d] = iso.split('-');
  return m && d ? `${d}/${m}` : iso;
}

export function sejour(from: string | null, to: string | null): string {
  if (!from || !to) return '—';
  return `${shortDate(from)} → ${shortDate(to)}`;
}

/**
 * The room column: the MAIN room, with the party's other rooms underneath.
 *
 * Operator, 2026-09-12 — *« the Main room should appear (in which case indicate
 * the related rooms underneath) »*. A two-room family is one réservation and
 * one line with the right number of breakfasts (0147), but until now the other
 * room was nowhere on the sheet: the salle laid one table and the étage knocked
 * on one door.
 *
 * Underneath rather than beside, because the room column is scanned vertically
 * — it is how the sheet is ordered — and a second number on the same line reads
 * as a different room, not as the same party.
 */
export function RoomCell({ row }: { row: ServiceListRow }) {
  const also = relatedRooms(row);
  return (
    <>
      {row.room}
      {also.length > 0 && <span className="service-rooms">+ {also.join(' · ')}</span>}
    </>
  );
}

function ServiceTable({
  service,
  title,
  countLabel,
  rows,
  children,
}: {
  service: 'dinner' | 'breakfast';
  title: string;
  countLabel: string;
  rows: ServiceListRow[];
  /** The group tables, on the breakfast half. Rendered INSIDE the section so
   *  the print rule that hides the other service takes them with it. */
  children?: React.ReactNode;
}) {
  const total = rows.reduce((n, r) => n + r.guests, 0);
  return (
    <section className={`service-sheet is-${service}`}>
      <h3>
        {title}
        {/* The total is part of the sheet, not a summary of it: the salle
            counts covers before it counts lines, and a printed page has no
            other way to add up. */}
        <span className="service-total">
          {total} {countLabel}
        </span>
      </h3>
      {rows.length === 0 ? (
        <p className="service-empty">Aucun {countLabel.toLowerCase()} ce jour-là.</p>
      ) : (
        <table className="service-table">
          <thead>
            <tr>
              <th>Nom</th>
              <th>Séjour</th>
              <th>Type</th>
              <th>Chb</th>
              <th className="is-num">{countLabel}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={`${r.room}-${r.party}-${i}`}>
                <td>{r.party}</td>
                <td>{sejour(r.stayFrom, r.stayTo)}</td>
                <td>{r.stayType}</td>
                <td>
                  <RoomCell row={r} />
                </td>
                <td className="is-num">{r.guests}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {children}
    </section>
  );
}


/**
 * One group's rooms, at the bottom of the breakfast half.
 *
 * Operator, 2026-09-12: *« for Groups, add a section at the bottom for each
 * Group as a table, with a smaller font, and just the Room and pdj cols »*.
 *
 * ⚠ The per-room figures and the group's own total come from DIFFERENT sources
 * and can disagree — plan versus effectif (0158's header; 0139 measured how
 * often, and in both directions). Where they do, the sheet SAYS so instead of
 * choosing: the total is the measured one, the rooms are the plan, and the
 * person carrying the tray is the only one who can reconcile them. Hiding the
 * gap would hand them a table that adds up to the wrong number and looks right.
 */
function GroupTable({ name, rooms }: { name: string; rooms: ServiceGroupRoom[] }) {
  const planned = rooms.reduce((n, r) => n + r.pdj, 0);
  const measured = rooms[0]?.effectifTotal ?? null;
  const mismatch = measured != null && measured !== planned;
  return (
    <section className="service-group">
      <h4>
        {name}
        <span className="service-total">
          {measured ?? planned} Pdj
          {mismatch && (
            <span
              className="service-gap"
              title={
                'Le total mesuré (effectif Vega) et la somme des chambres (type ' +
                'de séjour) diffèrent. Aucun effectif par chambre n’existe : à ' +
                'vérifier sur place.'
              }
            >
              chambres : {planned}
            </span>
          )}
        </span>
      </h4>
      <table className="service-table is-group">
        <thead>
          <tr>
            <th>Chb</th>
            <th className="is-num">Pdj</th>
          </tr>
        </thead>
        <tbody>
          {rooms.map((r) => (
            <tr key={r.room}>
              <td>{r.room}</td>
              <td className="is-num">{r.pdj}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}

type Service = 'dinner' | 'breakfast';

/**
 * « Effectifs » — its own page, its own day (operator, 2026-09-12).
 *
 * It used to be a button on the Planning board that REPLACED the board while it
 * was open, which was a workaround for `window.print()` printing the whole
 * document: anything still rendered ends up on the paper. As a page of its own
 * the workaround is unnecessary, and the sheet gets the two things a board
 * button could never give it — a day picker that is not the board's, and a
 * place in the nav for the people who print it.
 *
 * The board keeps the figures. This answers who is behind them.
 */
export function Effectifs() {
  /** The NIGHT. Dinner is served on it; breakfast the morning after. */
  const [day, setDay] = useState(() => todayIsoParis());
  const morning = shiftDay(day, 1);

  const board = useBoard();
  const [dinner, setDinner] = useState<ServiceListRow[] | null>(null);
  const [breakfast, setBreakfast] = useState<ServiceListRow[] | null>(null);
  const [groups, setGroups] = useState<ServiceGroupRoom[] | null>(null);
  const [err, setErr] = useState('');

  /**
   * Which service is going to paper, or null on screen.
   *
   * Two buttons, not one, because the two sheets are printed at DIFFERENT
   * TIMES (operator, 2026-09-09) — the breakfast list in the evening or early
   * morning, the dinner list before service. Printing both together means the
   * salle carries a page it does not need yet and, worse, a stale one: a sheet
   * printed hours early is a sheet whose covers have moved.
   */
  const [printing, setPrinting] = useState<Service | null>(null);

  // The class has to be on the DOM BEFORE the print dialog snapshots the page,
  // so the print is fired from an effect rather than from the click. `afterprint`
  // clears it; the timeout is the fallback for browsers that never fire it,
  // because a sheet class left behind would hide half the page on screen.
  useEffect(() => {
    if (!printing) return;
    const clear = () => setPrinting(null);
    window.addEventListener('afterprint', clear, { once: true });
    const fire = window.setTimeout(() => window.print(), 0);
    const giveUp = window.setTimeout(clear, 60_000);
    return () => {
      window.removeEventListener('afterprint', clear);
      window.clearTimeout(fire);
      window.clearTimeout(giveUp);
    };
  }, [printing]);

  // TWO dates, three calls. The dinner sheet is the night's; the breakfast
  // sheet and the group tables are the morning after's — see the header.
  useEffect(() => {
    let cancelled = false;
    setDinner(null);
    setBreakfast(null);
    setGroups(null);
    setErr('');
    void (async () => {
      try {
        const [d, b, g] = await Promise.all([
          board.serviceList(day),
          board.serviceList(morning),
          board.serviceGroupRooms(morning),
        ]);
        if (cancelled) return;
        setDinner(d.filter((r) => r.service === 'dinner'));
        setBreakfast(b.filter((r) => r.service === 'breakfast'));
        setGroups(g);
      } catch (e) {
        if (!cancelled) setErr(e instanceof Error ? e.message : String(e));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [board, day, morning]);

  // One table per group, in the order the database returned them (by name,
  // then room). `resa` is the key, not the name: two coach parties can carry
  // the same label and must not merge into one table.
  const groupTables = useMemo(() => {
    const byResa = new Map<string, ServiceGroupRoom[]>();
    for (const r of groups ?? []) {
      const list = byResa.get(r.resa);
      if (list) list.push(r);
      else byResa.set(r.resa, [r]);
    }
    return [...byResa.entries()];
  }, [groups]);

  const loading = dinner === null || breakfast === null || groups === null;

  return (
    <div className={`service-lists${printing ? ` printing-${printing}` : ''}`}>
      <div className="staff-daybar app-noprint">
        <button type="button" onClick={() => setDay(shiftDay(day, -1))} aria-label="Jour précédent">
          ‹
        </button>
        <FrDateInput className="pt-datepicker" value={day} onChange={(v) => v && setDay(v)} />
        <button type="button" onClick={() => setDay(shiftDay(day, 1))} aria-label="Jour suivant">
          ›
        </button>
        <button type="button" onClick={() => setDay(todayIsoParis())}>
          Aujourd’hui
        </button>
      </div>

      <div className="service-actions">
        <button type="button" onClick={() => setPrinting('dinner')} disabled={loading}>
          Imprimer le dîner
        </button>
        <button type="button" onClick={() => setPrinting('breakfast')} disabled={loading}>
          Imprimer les petits-déjeuners
        </button>
      </div>

      {err && <p className="auth-err">Erreur : {err}</p>}
      {!err && loading && <p className="staff-empty">Chargement…</p>}
      {!loading && (
        <>
          {/* Each half states its OWN date, in full, in its heading — and the
              heading is inside the printable section, so it reaches the paper.
              The two halves are two different days and a sheet that said so
              only once would be read as one. */}
          <ServiceTable
            service="dinner"
            title={`Dîner — ${longDayLabel(day)}`}
            countLabel="Couverts"
            rows={dinner}
          />
          <ServiceTable
            service="breakfast"
            title={`Petits-déjeuners — ${longDayLabel(morning)}`}
            countLabel="Pdj"
            rows={breakfast}
          >
            {groupTables.length > 0 && (
              <div className="service-groups">
                {groupTables.map(([resa, rooms]) => (
                  <GroupTable key={resa} name={rooms[0]?.groupName ?? resa} rooms={rooms} />
                ))}
              </div>
            )}
          </ServiceTable>
        </>
      )}
    </div>
  );
}


// ── The same sheet on a phone (staff.*) ───────────────────────────────────
//
// Operator, 2026-09-09: *"can it land in staff.* without the Name col on two
// different tab Diner / PdJ (no printing, must fit on phone)"*.
//
// Three deliberate differences from the printable sheet above, and they are all
// the same difference: this is READ, not CARRIED.
//
//   * **No « Nom ».** It is the widest column and the least useful here — a
//     cook checking « how many at 0205 » identifies the party by its room. It
//     is also the only column that names a guest, so leaving it out narrows
//     what the phone app shows to figures and room numbers. ⚠ The name is still
//     FETCHED — `board_service_list` returns it and this component drops it. If
//     the intent is privacy rather than width, that is a different change and
//     the name must stop leaving the database.
//   * **Tabs, not both lists.** A phone shows one thing at a time, and the two
//     services are consulted at different moments anyway.
//   * **No printing.** Nothing to hide from the page, so no print CSS.

export function ServiceTabs({ day }: { day: string }) {
  const board = useBoard();
  const [rows, setRows] = useState<ServiceListRow[] | null>(null);
  const [err, setErr] = useState('');
  const [tab, setTab] = useState<Service>('dinner');

  useEffect(() => {
    let cancelled = false;
    setRows(null);
    setErr('');
    void (async () => {
      try {
        const r = await board.serviceList(day);
        if (!cancelled) setRows(r);
      } catch (e) {
        if (!cancelled) setErr(e instanceof Error ? e.message : String(e));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [board, day]);

  const shown = (rows ?? []).filter((r) => r.service === tab);
  const total = shown.reduce((n, r) => n + r.guests, 0);
  const countLabel = tab === 'dinner' ? 'Couverts' : 'Pdj';

  return (
    <section className="service-tabs">
      <div className="service-tabbar">
        <button
          type="button"
          className={tab === 'dinner' ? 'is-active' : ''}
          onClick={() => setTab('dinner')}
        >
          Dîner
        </button>
        <button
          type="button"
          className={tab === 'breakfast' ? 'is-active' : ''}
          onClick={() => setTab('breakfast')}
        >
          Petits-déjeuners
        </button>
        {rows && (
          <span className="service-total">
            {total} {countLabel}
          </span>
        )}
      </div>

      {err && <p className="auth-err">Erreur : {err}</p>}
      {!err && rows === null && <p className="staff-empty">Chargement…</p>}
      {rows && shown.length === 0 && (
        <p className="service-empty">Personne ce jour-là.</p>
      )}
      {rows && shown.length > 0 && (
        <table className="service-table is-compact">
          <thead>
            <tr>
              <th>Séjour</th>
              <th>Type</th>
              <th>Chb</th>
              <th className="is-num">{countLabel}</th>
            </tr>
          </thead>
          <tbody>
            {shown.map((r, i) => (
              <tr key={`${r.room}-${i}`}>
                <td>{sejour(r.stayFrom, r.stayTo)}</td>
                <td>{r.stayType}</td>
                <td>
                  <RoomCell row={r} />
                </td>
                <td className="is-num">{r.guests}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}
